import { env } from "@canucci/base/env";
import { useCanvasBackLink, useCanvasSubtitle } from "@/layouts/CanvasLayoutContext";
import { routes } from "@/routes/paths";
import { BorderedCard } from "@/ui";
import { focusBackgroundAtIndex } from "@/utils/focusPatterns";
import { Badge, Box, Center, Divider, Grid, Group, Slider, Stack, Text, Title, useMantineTheme } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

type Alignment = "High agreement" | "Mixed" | "Niche";

type SuggestedAction = {
  title: string;
  description: string;
  impact: number;
  effort: number;
};

type PlannedAction = {
  title: string;
  owner: string;
  timeframe: string;
  status: "Not started" | "In progress" | "Done";
  success?: string;
};

type Priority = {
  id: string;
  title: string;
  summary: string;
  stars: number;
  respondents: number;
  alignment: Alignment;
  contextBullets: string[];
  quotes: string[];
  suggestedActions: SuggestedAction[];
  planned: PlannedAction[];
};

type ActionPlanData = {
  title: string;
  header: string;
  publishedAt: string;
  facilitator: string;
  priorities: Priority[];
};

function AlignmentBadge({ value }: { value: Alignment }) {
  const colorScheme = value === "High agreement" ? "green" : value === "Mixed" ? "orange" : "gray";
  return (
    <Badge color={colorScheme} size="xs">
      {value}
    </Badge>
  );
}

function ActionSlider({ label, value }: { label: string; value: number }) {
  return (
    <Stack align="start" gap="xs" w="100%">
      <Text fz="xs" c="gray.6">
        {label}
      </Text>
      <Slider
        value={value}
        min={1}
        max={3}
        step={1}
        w="100%"
        label={null}
        marks={[
          { value: 1, label: "" },
          { value: 3, label: "" },
        ]}
      />
      <Group fz="xs" c="gray.5" gap="xs" justify="space-between" w="100%">
        <Text>1 = Low</Text>
        <Text>3 = High</Text>
      </Group>
    </Stack>
  );
}

function ActionPlanPage() {
  const { canvasId } = useParams<{ canvasId: string }>();
  const { t: tPages } = useTranslation("pages");
  const [actionPlan, setActionPlan] = useState<ActionPlanData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const theme = useMantineTheme();
  const { sizes, spacing, semanticColors } = theme.other;
  useEffect(() => {
    const controller = new AbortController();
    const loadActionPlan = async () => {
      try {
        const response = await fetch(`${env.BASE_URL ?? "/"}action-plan-demo.json`, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data: ActionPlanData = await response.json();
        setActionPlan(data);
        setSelectedId(data.priorities[0]?.id ?? null);
        setError(null);
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };

    loadActionPlan();
    return () => controller.abort();
  }, []);

  useCanvasSubtitle(actionPlan?.header ?? "");

  useCanvasBackLink(tPages("canvas.overview.backToCanvas"), canvasId ? routes.canvas.overview.path(canvasId) : "/");

  const selectedPriority = useMemo(
    () => actionPlan?.priorities.find((priority) => priority.id === selectedId) ?? actionPlan?.priorities[0],
    [actionPlan, selectedId],
  );

  const backgroundImage =
    selectedPriority?.id === "p1"
      ? focusBackgroundAtIndex(1)
      : selectedPriority?.id === "p2"
        ? focusBackgroundAtIndex(2)
        : focusBackgroundAtIndex(3);

  if (loading) {
    return (
      <Center mih="100%">
        <Text>Loading action plan…</Text>
      </Center>
    );
  }

  if (error || !actionPlan) {
    return (
      <Center mih="100%">
        <Text c="red.6">Unable to load action plan{error ? `: ${error}` : ""}</Text>
      </Center>
    );
  }

  return (
    <Box
      mih="100%"
      style={{ background: `url(${backgroundImage}) top left repeat` }}
      py={{ base: "1.5rem", md: "2.5rem" }}
    >
      <Stack maw={sizes.maxPageWidthDesktop} mx="auto" px={{ base: "md", md: "xl" }} gap={spacing.pageGap}>
        <Group justify="space-between" align="center" c="white">
          <Title size="h1" c="white">
            {actionPlan.title}
          </Title>
          <Text fz="sm">{actionPlan.publishedAt}</Text>
        </Group>

        <Grid gutter={spacing.pageGap}>
          <Grid.Col span={{ base: 12, md: 4.4 }}>
            <Stack gap="md">
              {actionPlan.priorities.map((priority, idx) => {
                const isActive = priority.id === selectedId;
                const percentage = Math.round((priority.stars / priority.respondents) * 100);
                return (
                  <BorderedCard
                    key={priority.id}
                    p="md"
                    bg={isActive ? "white" : "gray.0"}
                    radius="lg"
                    shadow={isActive ? "sm" : undefined}
                    style={{
                      borderColor: isActive ? "var(--mantine-color-blue-2)" : semanticColors.borderSubtle,
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                    onClick={() => setSelectedId(priority.id)}
                  >
                    <Group justify="space-between" align="start" mb="xs">
                      <Text fw={600}>{priority.title}</Text>
                      <AlignmentBadge value={priority.alignment} />
                    </Group>
                    <Text fz="sm" c="gray.7" mb="sm">
                      {priority.summary}
                    </Text>
                    <Group gap="sm" wrap="wrap">
                      <Badge color="yellow">{priority.stars} stars</Badge>
                      <Badge color="gray">
                        {percentage}% of {priority.respondents}
                      </Badge>
                      <Badge color="blue">#{idx + 1} priority</Badge>
                    </Group>
                  </BorderedCard>
                );
              })}
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 7.6 }}>
            <BorderedCard p={{ base: "md", md: "lg" }} radius="lg">
              <Stack align="stretch" gap="md">
                <Box>
                  <Group justify="space-between" align="center" mb="xs">
                    <Title size="h4">Actions for: {selectedPriority?.title}</Title>
                    <Badge color="green" variant="light">
                      Draft curated by {actionPlan.facilitator}
                    </Badge>
                  </Group>
                  <Text fz="sm" c="gray.7">
                    {selectedPriority?.summary}
                  </Text>
                </Box>

                <Divider />

                <Box>
                  <Text fw={500} mb="xs">
                    Why this matters
                  </Text>
                  <Stack gap="xs">
                    {selectedPriority?.contextBullets.map((bullet) => (
                      <Group key={bullet} align="start" gap="xs">
                        <Box
                          mt="xs"
                          w="0.5rem"
                          h="0.5rem"
                          style={{
                            borderRadius: "50%",
                            backgroundColor: "var(--mantine-color-blue-4)",
                            flexShrink: 0,
                          }}
                        />
                        <Text fz="sm" c="gray.7">
                          {bullet}
                        </Text>
                      </Group>
                    ))}
                    <BorderedCard radius="md" bg="gray.0" p="xs">
                      <Stack gap="xs">
                        {selectedPriority?.quotes.map((quote) => (
                          <Text key={quote} fz="sm" c="gray.7">
                            {quote}
                          </Text>
                        ))}
                      </Stack>
                    </BorderedCard>
                  </Stack>
                </Box>

                <Divider />

                <Box>
                  <Text fw={500} mb="sm">
                    Suggested actions (drafts)
                  </Text>
                  <Stack gap="sm">
                    {selectedPriority?.suggestedActions.map((action) => (
                      <BorderedCard key={action.title} p="xs" bg="gray.0" radius="md">
                        <Text fw={600} mb="xs">
                          {action.title}
                        </Text>
                        <Text fz="sm" c="gray.7" mb="sm">
                          {action.description}
                        </Text>
                        <Group gap="sm" align="start">
                          <ActionSlider key={`${action.title}-impact`} label="Impact" value={action.impact} />
                          <ActionSlider key={`${action.title}-effort`} label="Effort" value={action.effort} />
                        </Group>
                      </BorderedCard>
                    ))}
                  </Stack>
                </Box>

                <Divider />

                <Box>
                  <Text fw={500} mb="sm">
                    Plan for this priority
                  </Text>
                  <Stack gap="sm">
                    {selectedPriority?.planned.map((planned) => (
                      <BorderedCard key={planned.title} p="xs" radius="md">
                        <Group justify="space-between" align="start" mb="xs">
                          <Text fw={600}>{planned.title}</Text>
                          <Badge
                            color={
                              planned.status === "Done" ? "green" : planned.status === "In progress" ? "blue" : "gray"
                            }
                          >
                            {planned.status}
                          </Badge>
                        </Group>
                        <Text fz="sm" c="gray.7" mb="xs">
                          Owner: {planned.owner} • {planned.timeframe}
                        </Text>
                        {planned.success && (
                          <Text fz="sm" c="gray.6">
                            How we'll know it worked: {planned.success}
                          </Text>
                        )}
                      </BorderedCard>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </BorderedCard>
          </Grid.Col>
        </Grid>
      </Stack>
    </Box>
  );
}

export default ActionPlanPage;
